import { createClientOnlyFn } from '@tanstack/react-start';
import { devtools } from 'zustand/middleware';
import { createWithEqualityFn } from 'zustand/traditional';

import type { UserInstrumentDisplayDataPayload } from '~/integrations/yahoo/instrument-display-data-payload.ts';
import type { InstrumentDisplayDataResponse } from '~/integrations/yahoo/queries.js';

import deferredPersist from './defered-persist.ts';
import type { IndexedDbInstrumentSnapshot } from './instrument-cache.ts';
import type { CachedLatestPriceResponse } from './price-cache.ts';
import { mergeCachedLatestIntoFetch } from './price-cache.ts';
import type {
	InstrumentSocketResponse,
	MergedInstrumentType,
	PortfolioGraphCardKey,
	PortfoliosExpandedAttrs,
	PortfoliosSelectedAttrs,
	PortfolioTableToggleableColumnKey,
} from './types.ts';
import { getMarketHoursFromHours, getMarketHoursFromState } from './utils.ts';

export interface StoreInterface {
	applyCachedLatestPrices: (prices: readonly CachedLatestPriceResponse[]) => void;
	applyInstrumentDisplayPayload: (payload: UserInstrumentDisplayDataPayload) => void;
	instrumentFetches: Map<string, InstrumentDisplayDataResponse>;
	initInstrumentFetches: (data: Map<string, InstrumentDisplayDataResponse> | undefined) => void;
	updateInstrumentFetch: (ticker: string, data: InstrumentDisplayDataResponse) => void;

	instrumentSockets: Map<string, InstrumentSocketResponse>;
	initInstrumentSocket: (tickers: string[]) => void;
	updateInstrumentSocket: (ticker: string, ws: InstrumentSocketResponse) => void;

	instrumentsMerged: Map<string, MergedInstrumentType>;
	updateInstrumentsMerged: (
		ticker: string,
		iSocket: InstrumentSocketResponse | null,
		iFetch: InstrumentDisplayDataResponse | null,
	) => void;

	getMergedInstrumentDataForTickers: (tickers: readonly string[]) => (MergedInstrumentType | undefined)[];
	getMissingInstrumentFetchTickers: (tickers: readonly string[]) => string[];

	tickerList: string[];
	addTicker: (ticker: string) => void;
	addTickers: (tickers: string[]) => void;
	setTickerList: (tickers: string[]) => void;

	portfoliosExpanded: PortfoliosExpandedAttrs;
	initPortfolioExpanded: (portfolioId: number) => void;
	setPortfolioExpanded: (portfolioId: number, id: string, expanded: boolean) => void;
	setPortfoliosExpanded: (portfoliosExpanded: PortfoliosExpandedAttrs) => void;

	portfoliosSelected: PortfoliosSelectedAttrs;
	clearPortfoliosSelected: (portfolioId: number, ticker: string) => void;
	setPortfoliosSelected: (
		portfolioId: number,
		ticker: string,
		values: {
			id: string;
			dbID: number;
		}[],
		count: number,
	) => void;

	selectedCount: Map<number, number>;
	setSelectedCount: (portfolioId: number, count: number) => void;

	portfolioGraphCardOverrides: Map<number, Partial<Record<PortfolioGraphCardKey, boolean>>>;
	setPortfolioGraphCardVisibility: (portfolioId: number, cardKey: PortfolioGraphCardKey, visible: boolean) => void;
	portfolioTableColumnVisibilityOverrides: Map<number, Partial<Record<PortfolioTableToggleableColumnKey, boolean>>>;
	setPortfolioTableColumnVisibility: (
		portfolioId: number,
		columnKey: PortfolioTableToggleableColumnKey,
		visible: boolean,
	) => void;

	// UI
	sidebarType: 'mobile' | 'desktop';
	setSidebarType: (sidebarType: 'mobile' | 'desktop') => void;
	isDesktopExtended: boolean;
	toggleDesktopExtended: () => void;
}

let resolveHydratePromise: (() => void) | undefined;
const HydratePromise = new Promise<void>((resolve) => {
	resolveHydratePromise = resolve;
});

export const ResolveIsHydrated = resolveHydratePromise || (() => {});

let resolvePersistHydratedPromise: (() => void) | undefined;
const PersistHydratedPromise = new Promise<void>((resolve) => {
	resolvePersistHydratedPromise = resolve;
});

const PERSISTED_INSTRUMENT_LIMIT = 400;
const PERSIST_DEBOUNCE_MS = 10_000;
const LOCAL_MODE_INDEXED_DB_LIMIT = 4_000;

interface PersistedStoreState {
	instrumentsMerged: Map<string, MergedInstrumentType>;
	isDesktopExtended: boolean;
	sidebarType: 'mobile' | 'desktop';
	tickerList: string[];
}

function createPersistedState(state: StoreInterface): PersistedStoreState {
	if (isLocalModeEnabled()) {
		return {
			instrumentsMerged: new Map(),
			isDesktopExtended: state.isDesktopExtended,
			sidebarType: state.sidebarType,
			tickerList: [],
		};
	}

	const mergedEntries = Array.from(state.instrumentsMerged.entries())
		.sort((a, b) => (b[1]?.time ?? 0) - (a[1]?.time ?? 0))
		.slice(0, PERSISTED_INSTRUMENT_LIMIT);

	const instrumentsMerged = new Map(mergedEntries);
	const allowedTickers = new Set(mergedEntries.map(([ticker]) => ticker));
	const tickerList = state.tickerList.filter((ticker) => allowedTickers.has(ticker));

	return {
		instrumentsMerged,
		isDesktopExtended: state.isDesktopExtended,
		sidebarType: state.sidebarType,
		tickerList: tickerList.length > 0 ? tickerList : mergedEntries.map(([ticker]) => ticker),
	};
}

function isLocalModeEnabled(): boolean {
	return typeof window !== 'undefined' && window.ENV.LOCAL_MODE;
}

function createIndexedDbPersistedState(state: StoreInterface): IndexedDbInstrumentSnapshot {
	const mergedEntries = Array.from(state.instrumentsMerged.entries())
		.sort((a, b) => (b[1]?.time ?? 0) - (a[1]?.time ?? 0))
		.slice(0, LOCAL_MODE_INDEXED_DB_LIMIT);

	const allowedTickers = new Set(mergedEntries.map(([ticker]) => ticker));
	const tickerList = state.tickerList.filter((ticker) => allowedTickers.has(ticker));

	return {
		instrumentsMerged: mergedEntries,
		tickerList: tickerList.length > 0 ? tickerList : mergedEntries.map(([ticker]) => ticker),
		updatedAt: Date.now(),
	};
}

const readInstrumentSnapshotClient = createClientOnlyFn(async () => {
	const { readInstrumentSnapshot } = await import('./instrument-cache.client.ts');
	return readInstrumentSnapshot();
});

const writeInstrumentSnapshotClient = createClientOnlyFn(async (snapshot: IndexedDbInstrumentSnapshot) => {
	const { writeInstrumentSnapshot } = await import('./instrument-cache.client.ts');
	await writeInstrumentSnapshot(snapshot);
});

async function persistInstrumentSnapshotToIndexedDb(state: StoreInterface): Promise<void> {
	if (!isLocalModeEnabled()) {
		return;
	}

	const snapshot = createIndexedDbPersistedState(state);
	await writeInstrumentSnapshotClient(snapshot);
}

export const useStore = createWithEqualityFn<StoreInterface>()(
	devtools(
		deferredPersist(
			(set, get) => ({
				addTicker: (val) => set((state) => mergeTickerList(state.tickerList, val)),
				addTickers: (val) => set((state) => mergeTickerList(state.tickerList, val)),
				applyCachedLatestPrices: (prices) =>
					set(({ instrumentFetches, instrumentsMerged }) => {
						let hasChanges = false;
						let nextInstrumentFetches = instrumentFetches;
						let nextInstrumentsMerged = instrumentsMerged;

						for (const cachedPrice of prices) {
							if (cachedPrice.staleAt <= Date.now()) {
								continue;
							}

							const existingFetch = nextInstrumentFetches.get(cachedPrice.symbol);
							if (!existingFetch) {
								continue;
							}

							if (!hasChanges) {
								nextInstrumentFetches = new Map(instrumentFetches);
								hasChanges = true;
							}

							const nextFetch = mergeCachedLatestIntoFetch(existingFetch, cachedPrice);
							nextInstrumentFetches.set(cachedPrice.symbol, nextFetch);
							nextInstrumentsMerged = updateInstrumentsMerged(
								cachedPrice.symbol,
								null,
								nextFetch,
								nextInstrumentsMerged,
							).instrumentsMerged;
						}

						if (!hasChanges) {
							return {};
						}

						return {
							instrumentFetches: nextInstrumentFetches,
							instrumentsMerged: nextInstrumentsMerged,
						};
					}),
				applyInstrumentDisplayPayload: (payload) =>
					set(({ instrumentFetches, instrumentsMerged, tickerList }) => {
						const nextInstrumentFetches =
							payload.instrumentFetchEntries.length > 0 ? new Map(instrumentFetches) : instrumentFetches;
						let nextInstrumentsMerged = instrumentsMerged;

						for (const [ticker, instrumentFetch] of payload.instrumentFetchEntries) {
							nextInstrumentFetches.set(ticker, instrumentFetch);
							nextInstrumentsMerged = updateInstrumentsMerged(
								ticker,
								null,
								instrumentFetch,
								nextInstrumentsMerged,
							).instrumentsMerged;
						}

						return {
							instrumentFetches: nextInstrumentFetches,
							instrumentsMerged: nextInstrumentsMerged,
							...mergeTickerList(tickerList, [
								...payload.tickerList,
								...payload.instrumentFetchEntries.map(([ticker]) => ticker),
							]),
						};
					}),
				clearPortfoliosSelected: (portfolioId, ticker) =>
					set(({ portfoliosSelected }) => {
						if (portfoliosSelected.has(portfolioId)) {
							portfoliosSelected.get(portfolioId)?.values.set(ticker, []);
							portfoliosSelected.get(portfolioId)?.activeCount.set(ticker, 0);
						} else {
							portfoliosSelected.set(portfolioId, {
								activeCount: new Map(),
								values: new Map(),
							});
						}
						return { portfoliosSelected };
					}),
				getMergedInstrumentDataForTickers: (tickers) =>
					getMergedInstrumentDataForTickers(get().instrumentsMerged, tickers),
				getMissingInstrumentFetchTickers: (tickers) =>
					getMissingInstrumentFetchTickers(get().instrumentFetches, tickers),
				initInstrumentFetches: (data) =>
					set(({ updateInstrumentsMerged }) => {
						const initMap = new Map();
						data?.forEach((value, key) => {
							updateInstrumentsMerged(key, null, value);
							initMap.set(key, value);
						});
						return { instrumentFetches: initMap };
					}),
				initInstrumentSocket: (tickers) =>
					set(() => {
						const initMap = new Map();
						tickers.forEach((ticker) => {
							initMap.set(ticker, {});
						});
						return { instrumentSockets: initMap };
					}),
				initPortfolioExpanded: (portfolioId) =>
					set(({ portfoliosExpanded }) => {
						if (!portfoliosExpanded.has(portfolioId)) {
							portfoliosExpanded.set(portfolioId, new Map<string, boolean>());
						}
						return { portfoliosExpanded };
					}),
				// Handle InstrumentFetches
				instrumentFetches: new Map(),

				// Handle InstrumentSockets
				instrumentSockets: new Map(),

				// Handle InstrumentsMerged, combination of fetch + socket data
				instrumentsMerged: new Map(),
				isDesktopExtended: false as boolean,
				portfolioGraphCardOverrides: new Map(),

				// Handle PortfoliosExpanded
				portfoliosExpanded: new Map(),

				// Handle PortfoliosSelected
				portfoliosSelected: new Map(),
				portfolioTableColumnVisibilityOverrides: new Map(),

				// Handle SelectedCount (portfolio's transactions)
				selectedCount: new Map(),
				setPortfolioExpanded: (portfolioId, id, expanded) =>
					set(({ portfoliosExpanded }) => {
						portfoliosExpanded.get(portfolioId)?.set(id, expanded);
						return { portfoliosExpanded };
					}),
				setPortfolioGraphCardVisibility: (portfolioId, cardKey, visible) =>
					set(({ portfolioGraphCardOverrides }) => {
						const nextOverrides = new Map(portfolioGraphCardOverrides);
						const currentPortfolioOverride = nextOverrides.get(portfolioId) ?? {};

						nextOverrides.set(portfolioId, {
							...currentPortfolioOverride,
							[cardKey]: visible,
						});

						return {
							portfolioGraphCardOverrides: nextOverrides,
						};
					}),
				setPortfoliosExpanded: (portfoliosExpanded) => set({ portfoliosExpanded }),
				setPortfoliosSelected: (portfolioId, ticker, values, count) =>
					set(({ portfoliosSelected }) => {
						if (portfoliosSelected.has(portfolioId)) {
							portfoliosSelected.get(portfolioId)?.values.set(ticker, values);
							portfoliosSelected.get(portfolioId)?.activeCount.set(ticker, count);
						} else {
							portfoliosSelected.set(portfolioId, {
								activeCount: new Map(),
								values: new Map(),
							});
						}
						return { portfoliosSelected };
					}),
				setPortfolioTableColumnVisibility: (portfolioId, columnKey, visible) =>
					set(({ portfolioTableColumnVisibilityOverrides }) => {
						const nextOverrides = new Map(portfolioTableColumnVisibilityOverrides);
						const currentPortfolioOverride = nextOverrides.get(portfolioId) ?? {};

						nextOverrides.set(portfolioId, {
							...currentPortfolioOverride,
							[columnKey]: visible,
						});

						return {
							portfolioTableColumnVisibilityOverrides: nextOverrides,
						};
					}),
				setSelectedCount: (portfolioId, count) =>
					set(({ selectedCount }) => ({
						selectedCount: selectedCount.set(portfolioId, count),
					})),
				setSidebarType: (sidebarType) => {
					set({ sidebarType });
				},
				setTickerList: (tickers) => set(() => ({ tickerList: tickers })),

				// UI
				sidebarType: 'mobile',

				// Handle TickerList (which is what websocket listens to)
				tickerList: [] as string[],
				toggleDesktopExtended: () =>
					set((state) => ({
						isDesktopExtended: !state.isDesktopExtended,
					})),
				updateInstrumentFetch: (ticker, data) =>
					set(({ updateInstrumentsMerged, instrumentFetches }) => {
						updateInstrumentsMerged(ticker, null, data);
						return {
							instrumentFetches: new Map(instrumentFetches).set(ticker, data),
						};
					}),
				updateInstrumentSocket: (ticker, ws) =>
					set(({ updateInstrumentsMerged, instrumentSockets }) => {
						updateInstrumentsMerged(ticker, ws, null);
						return {
							instrumentSockets: new Map(instrumentSockets).set(ticker, ws),
						};
					}),
				updateInstrumentsMerged: (ticker, iSocket, iFetch) =>
					set(({ instrumentsMerged }) => updateInstrumentsMerged(ticker, iSocket, iFetch, instrumentsMerged)),
			}),
			{
				debounceMs: PERSIST_DEBOUNCE_MS,
				hydrateOnResolve: HydratePromise,
				name: 'instrument-storage',
				onRehydrateStorage: () => () => {
					resolvePersistHydratedPromise?.();
				},
				persistToIndexedDb: persistInstrumentSnapshotToIndexedDb,
				preparePersistState: createPersistedState,
			},
		),
	),
);

async function waitForStoreHydration(): Promise<void> {
	await PersistHydratedPromise;
}

export async function hydrateLocalModeInstrumentCache(): Promise<void> {
	if (!isLocalModeEnabled()) {
		return;
	}

	await waitForStoreHydration();

	const snapshot = await readInstrumentSnapshotClient();
	if (!snapshot) {
		return;
	}

	useStore.setState((state) => {
		const nextInstrumentsMerged = new Map(state.instrumentsMerged);
		for (const [ticker, cachedValue] of snapshot.instrumentsMerged) {
			const currentValue = nextInstrumentsMerged.get(ticker);
			if (!currentValue || cachedValue.time > currentValue.time) {
				nextInstrumentsMerged.set(ticker, cachedValue);
			}
		}

		const nextTickerList = Array.from(new Set([...state.tickerList, ...snapshot.tickerList]));

		return {
			instrumentsMerged: nextInstrumentsMerged,
			tickerList: nextTickerList,
		};
	});
}

// Helper functions

/**
 * Merge two ticker lists
 * @param a
 * @param b
 * @returns
 */
const mergeTickerList = (a: string[], b: string[] | string) => {
	const tickers = typeof b === 'string' ? [b] : b;
	const merged = Array.from(new Set([...a, ...tickers]));

	if (merged.length === a.length && merged.every((ticker, index) => ticker === a[index])) {
		return { tickerList: a };
	}

	return { tickerList: merged };
};

/**
 * Update instruments merged by combining latest fetch and socket.
 * @param ticker
 * @param iSocket
 * @param iFetch
 * @param instrumentsMerged
 * @returns
 */
const updateInstrumentsMerged = (
	ticker: string,
	iSocket: InstrumentSocketResponse | null,
	iFetch: InstrumentDisplayDataResponse | null,
	instrumentsMerged: Map<string, MergedInstrumentType>,
) => {
	if (iSocket) {
		const socketTime = parseInt(iSocket.time, 10) ?? 0;
		const socketMarketHours = getMarketHoursFromHours(iSocket.marketHours) ?? '';

		const previousValue = instrumentsMerged.get(ticker);

		const instrumentMerged = {
			ask: iSocket.ask, // Both
			askSize: iSocket.askSize, // Both
			basis: 'BOTH', // Both
			bid: iSocket.bid, // Both
			bidSize: iSocket.bidSize, // Both
			change: iSocket.change, // Both
			changePercent: iSocket.changePercent, // Both
			currency: iSocket.currency ?? previousValue?.currency ?? null, // Both
			exchange: iSocket.exchange, // Both
			fullExchangeName: previousValue?.fullExchangeName, // Fetch
			longName: previousValue?.longName, // Both
			marketCap: iSocket.marketcap, // Both
			marketHours: socketMarketHours, // Both
			postMarketChange:
				socketMarketHours === 2 || socketMarketHours === 3 ? iSocket.change : previousValue?.postMarketChange, // Both
			postMarketChangePercent:
				socketMarketHours === 2 || socketMarketHours === 3
					? iSocket.changePercent
					: previousValue?.postMarketChangePercent, // Both
			postMarketPrice:
				socketMarketHours === 2 || socketMarketHours === 3 ? iSocket.price : previousValue?.postMarketPrice, // Fetch
			postMarketTime: socketMarketHours === 2 || socketMarketHours === 3 ? iSocket.time : previousValue?.postMarketTime, // Fetch
			preMarketChange: socketMarketHours === 0 ? iSocket.change : previousValue?.preMarketChange, // Fetch
			preMarketChangePercent: socketMarketHours === 0 ? iSocket.changePercent : previousValue?.preMarketChangePercent, // Both
			preMarketPrice: socketMarketHours === 0 ? iSocket.price : previousValue?.preMarketPrice, // Both
			preMarketTime: socketMarketHours === 0 ? iSocket.time : previousValue?.preMarketTime, // Both (marketHours && marketState)
			price: iSocket.price, // Fetch
			priceHint: iSocket.priceHint, // Fetch
			quoteSourceName: previousValue?.quoteSourceName, // Fetch
			quoteType: iSocket.quoteType, // Fetch
			sharesOutstanding: previousValue?.sharesOutstanding, // Fetch
			shortName: previousValue?.shortName, // Fetch
			ticker: iSocket.id, // Fetch
			time: socketTime, // Fetch
			volume: iSocket.dayVolume ? parseInt(iSocket.dayVolume, 10) : previousValue ? previousValue.volume : null,
		} as MergedInstrumentType;

		return {
			instrumentsMerged: new Map(instrumentsMerged).set(ticker, instrumentMerged),
		};
	} else if (iFetch) {
		//TODO: Check if this new value is better than prev.

		const fetchTime = iFetch ? iFetch.regularMarketTime * 1000 : 0;

		const instrumentMerged = {
			ask: iFetch.ask, // Both
			askSize: iFetch.askSize, // Both
			basis: 'BOTH', // Both
			bid: iFetch.bid, // Both
			bidSize: iFetch.bidSize, // Both
			change: iFetch.regularMarketChange, // Both
			changePercent: iFetch.regularMarketChangePercent, // Both
			currency: iFetch.currency, // Both
			exchange: iFetch.exchange, // Both
			fullExchangeName: iFetch.fullExchangeName, // Fetch
			longName: iFetch.longName,
			marketCap: iFetch.marketCap, // Fetch
			marketHours: getMarketHoursFromState(iFetch.marketState), // Both
			postMarketChange: iFetch.postMarketChange, // Both
			postMarketChangePercent: iFetch.postMarketChangePercent, // Both
			postMarketPrice: iFetch.postMarketPrice, // Fetch
			postMarketTime: iFetch.postMarketTime, // Fetch
			preMarketChange: iFetch.preMarketChange, // Fetch
			preMarketChangePercent: iFetch.preMarketChangePercent, // Both
			preMarketPrice: iFetch.preMarketPrice, // Both
			preMarketTime: iFetch.preMarketTime, // Both (marketHours && marketState)
			price: iFetch.regularMarketPrice, // Fetch
			priceHint: iFetch.priceHint, // Fetch
			quoteSourceName: iFetch.quoteSourceName, // Fetch
			quoteType: iFetch.quoteType, // Fetch
			sharesOutstanding: iFetch.sharesOutstanding, // Fetch
			shortName: iFetch.shortName, // Fetch
			ticker: iFetch.symbol, // Fetch
			time: fetchTime, // Fetch
			volume: iFetch.regularMarketVolume,
		} as MergedInstrumentType;

		return {
			instrumentsMerged: new Map(instrumentsMerged).set(ticker, instrumentMerged),
		};
	} else {
		return { instrumentsMerged };
	}
};

const getMergedInstrumentDataForTickers = (
	instrumentsMerged: Map<string, MergedInstrumentType>,
	tickers: readonly string[],
) => tickers.map((ticker) => instrumentsMerged.get(ticker));

const getMissingInstrumentFetchTickers = (
	instrumentFetches: Map<string, InstrumentDisplayDataResponse>,
	tickers: readonly string[],
) => tickers.filter((ticker) => !instrumentFetches.has(ticker));
